import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import {
  ShoppingCart, Globe, Settings, Loader2, X, Plus, Trash2, Save, History, Eye, Edit3, AlertCircle,
} from 'lucide-react';
import {
  getB2BAnalytics, getB2BRules, createB2BRule, updateB2BRule, deleteB2BRule, getB2BAudit, getB2BPreview,
} from '../api';

const monoFont = "'JetBrains Mono', 'Fira Code', monospace";
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDateBR = (s) => { if (!s) return ''; const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };

const UNIT_COLORS = {
  JMV: '#10b981',
  FIX_CSP: '#3b82f6',
  FIX_MSC: '#06b6d4',
  VIP: '#a855f7',
  TI: '#f59e0b',
  World: '#ec4899',
  Worldbra: '#ef4444',
  UNASSIGNED: '#6b7280',
};

function UnitChip({ unit, label, total, count }) {
  const c = UNIT_COLORS[unit] || '#6b7280';
  return (
    <div style={{
      background: `${c}10`, border: `1px solid ${c}40`, borderRadius: 10,
      padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140,
    }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: c, letterSpacing: '0.12em' }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', fontFamily: monoFont }}>{fmtBRL(total)}</span>
      <span style={{ fontSize: 10, color: '#475569', fontFamily: monoFont }}>{count} pedidos</span>
    </div>
  );
}

function ChartCard({ titulo, icon: Icon, accent, total, count, dados, onClickUnit }) {
  const dataFiltered = dados.filter(d => d.count > 0 || d.total > 0);
  return (
    <div style={{
      background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
      border: '1px solid #e2e8f0', borderRadius: 14, padding: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, background: `${accent}22`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}><Icon size={17} color={accent} /></div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{titulo}</p>
            <p style={{ fontSize: 11, color: '#475569', fontFamily: monoFont }}>{count} pedidos · {fmtBRL(total)}</p>
          </div>
        </div>
      </div>

      {dataFiltered.length === 0 ? (
        <p style={{ padding: 30, color: '#64748b', textAlign: 'center', fontSize: 13 }}>
          Sem pedidos classificados nesse período.
        </p>
      ) : (
        <>
          <div style={{ width: '100%', height: 220, marginTop: 8 }}>
            <ResponsiveContainer>
              <BarChart data={dataFiltered} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                <YAxis tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  cursor={{ fill: 'rgba(15,23,42,0.08)' }}
                  contentStyle={{
                    background: 'rgba(255,255,255,0.96)',
                    border: `1px solid ${accent}66`,
                    borderRadius: 10,
                    boxShadow: `0 8px 28px rgba(15,23,42,0.10), 0 0 0 1px ${accent}22`,
                    padding: '10px 14px',
                    backdropFilter: 'blur(8px)',
                  }}
                  labelStyle={{ color: '#0f172a', fontWeight: 800, fontSize: 13, marginBottom: 4 }}
                  itemStyle={{ color: '#b45309', fontWeight: 800, fontFamily: monoFont, fontSize: 14 }}
                  formatter={(v, name, p) => [fmtBRL(v), p.payload.label]}
                />
                <Bar dataKey="total" radius={[6, 6, 0, 0]} onClick={(d) => onClickUnit && onClickUnit(d.unit)} style={{ cursor: 'pointer' }}>
                  {dataFiltered.map((d, i) => <Cell key={i} fill={UNIT_COLORS[d.unit] || '#6b7280'} />)}
                  <LabelList dataKey="total" position="top" formatter={v => v >= 1000 ? `R$${(v/1000).toFixed(1)}k` : `R$${v.toFixed(0)}`} style={{ fontSize: 10, fill: '#334155', fontFamily: monoFont }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {dataFiltered.map(d => (
              <button key={d.unit} onClick={() => onClickUnit && onClickUnit(d.unit)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }} title="Ver pedidos desta unidade">
                <UnitChip {...d} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RulesModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // rule object or 'new'
  const [auditOpen, setAuditOpen] = useState(null); // rule id or 'all'
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getB2BRules(); setData(r.data); }
    catch (e) { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAudit = async (ruleId) => {
    setAuditOpen(ruleId || 'all');
    try { const r = await getB2BAudit(ruleId, 100); setAudit(r.data.audit || []); }
    catch { setAudit([]); }
  };

  const save = async (rule) => {
    if (editing === 'new') {
      await createB2BRule(rule, 'admin');
    } else {
      await updateB2BRule(editing.id, rule, 'admin');
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Deletar esta regra?')) return;
    await deleteB2BRule(id, 'admin');
    load();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 80,
      background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #e2e8f0', borderRadius: 16,
        width: '100%', maxWidth: 1200, maxHeight: '90vh',
        boxShadow: '0 24px 60px rgba(15,23,42,0.10)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Settings size={18} color="#60a5fa" />
            <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Regras de roteamento B2B/B2C</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => openAudit(null)} style={btnStyle('#475569')}>
              <History size={13} /> Auditoria geral
            </button>
            <button onClick={() => setEditing('new')} style={btnStyle('#10b981')}>
              <Plus size={13} /> Nova regra
            </button>
            <button onClick={onClose} aria-label="Fechar" style={{
              background: 'rgba(15,23,42,0.08)', border: 'none', borderRadius: 8,
              padding: 6, cursor: 'pointer', color: '#334155', display: 'inline-flex',
            }}><X size={16} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && <p style={{ padding: 30, color: '#475569', textAlign: 'center' }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} /> Carregando…</p>}
          {!loading && data && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f4f6fb', zIndex: 2 }}>
                <tr>
                  {['Prio.', 'Nome', 'Campo', 'Tipo', 'Valor', 'Escopo', 'Unidade', 'Status', 'Ações'].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 12px', textAlign: 'left',
                      borderBottom: '1px solid #e2e8f0', color: '#64748b',
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rules.map((r, idx) => (
                  <tr key={r.id} style={{ background: idx % 2 ? 'rgba(15,23,42,0.02)' : 'transparent', borderBottom: '1px solid rgba(226,232,240,0.8)', opacity: r.enabled ? 1 : 0.5 }}>
                    <td style={{ padding: '8px 12px', fontFamily: monoFont, color: '#b45309', fontWeight: 700 }}>{r.priority}</td>
                    <td style={{ padding: '8px 12px', color: '#0f172a' }}>{r.nome}</td>
                    <td style={{ padding: '8px 12px', color: '#475569', fontSize: 11 }}>{r.match_field}</td>
                    <td style={{ padding: '8px 12px', color: '#475569', fontSize: 11 }}>{r.match_type}</td>
                    <td style={{ padding: '8px 12px', color: '#334155', fontFamily: monoFont, fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.match_value}</td>
                    <td style={{ padding: '8px 12px', color: '#475569', fontSize: 11, textTransform: 'uppercase' }}>{r.site_scope}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '3px 9px', borderRadius: 999, background: `${UNIT_COLORS[r.target_unit]}22`, color: UNIT_COLORS[r.target_unit], fontSize: 10, fontWeight: 800 }}>
                        {(data.unidades.find(u => u.value === r.target_unit) || {}).label || r.target_unit}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 999, background: r.enabled ? 'rgba(16,185,129,0.18)' : 'rgba(148,163,184,0.18)', color: r.enabled ? '#15803d' : '#64748b', fontSize: 10, fontWeight: 800 }}>
                        {r.enabled ? 'ATIVA' : 'INATIVA'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', display: 'flex', gap: 6 }}>
                      <button onClick={() => openAudit(r.id)} title="Histórico" style={iconBtnStyle('#a855f7')}><History size={13} /></button>
                      <button onClick={() => setEditing(r)} title="Editar" style={iconBtnStyle('#60a5fa')}><Edit3 size={13} /></button>
                      <button onClick={() => remove(r.id)} title="Deletar" style={iconBtnStyle('#ef4444')}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '10px 22px', borderTop: '1px solid #e2e8f0', fontSize: 11, color: '#64748b' }}>
          Regras avaliadas por prioridade ascendente (10 antes de 20). A primeira que casar define a unidade. Sem match → "Não classificado".
        </div>
      </div>

      {editing && data && (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          data={data}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}

      {auditOpen !== null && (
        <AuditPanel audit={audit} onClose={() => setAuditOpen(null)} />
      )}
    </div>
  );
}

function btnStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '7px 12px', borderRadius: 8, border: `1px solid ${color}40`,
    background: `${color}15`, color, fontSize: 11.5, fontWeight: 700,
    cursor: 'pointer',
  };
}
function iconBtnStyle(color) {
  return {
    padding: 6, borderRadius: 6, border: 'none',
    background: `${color}15`, color, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center',
  };
}

function RuleEditor({ rule, data, onClose, onSave }) {
  const [form, setForm] = useState(() => rule ? { ...rule } : {
    priority: 100, nome: '', match_field: 'shipping_method', match_type: 'contains',
    match_value: '', case_sensitive: false, site_scope: 'all', target_unit: 'UNASSIGNED',
    enabled: true, descricao: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f4f6fb', color: '#0f172a', fontSize: 12.5, outline: 'none', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 90,
      background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14,
        width: '100%', maxWidth: 620, padding: 22, display: 'flex', flexDirection: 'column', gap: 14,
        maxHeight: '90vh', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{rule ? `Editar regra #${rule.id}` : 'Nova regra'}</p>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#475569' }}><X size={18} /></button>
        </div>
        <div>
          <label style={labelStyle}>Nome</label>
          <input style={inputStyle} value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Ex: FIX CSP — Porto Alegre Levar/Retirar" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Prioridade</label>
            <input type="number" style={inputStyle} value={form.priority} onChange={e => set('priority', parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <label style={labelStyle}>Unidade alvo</label>
            <select style={inputStyle} value={form.target_unit} onChange={e => set('target_unit', e.target.value)}>
              {data.unidades.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Campo</label>
            <select style={inputStyle} value={form.match_field} onChange={e => set('match_field', e.target.value)}>
              {data.match_fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Tipo de match</label>
            <select style={inputStyle} value={form.match_type} onChange={e => set('match_type', e.target.value)}>
              {data.match_types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Escopo</label>
            <select style={inputStyle} value={form.site_scope} onChange={e => set('site_scope', e.target.value)}>
              {data.site_scopes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingTop: 18 }}>
            <label style={{ display: 'flex', gap: 6, fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.case_sensitive} onChange={e => set('case_sensitive', e.target.checked)} /> case-sensitive
            </label>
            <label style={{ display: 'flex', gap: 6, fontSize: 12, color: '#334155', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} /> Ativa
            </label>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Valor (o que procurar)</label>
          <input style={inputStyle} value={form.match_value} onChange={e => set('match_value', e.target.value)} placeholder="Ex: Porto Alegre - Atendimento em Casa" />
        </div>
        <div>
          <label style={labelStyle}>Descrição (opcional)</label>
          <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.descricao || ''} onChange={e => set('descricao', e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={btnStyle('#475569')}>Cancelar</button>
          <button onClick={() => onSave(form)} style={btnStyle('#10b981')}><Save size={13} /> Salvar</button>
        </div>
      </div>
    </div>
  );
}

function AuditPanel({ audit, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(255,255,255,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14,
        width: '100%', maxWidth: 800, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <History size={16} color="#a855f7" />
            <p style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Auditoria de regras</p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#475569' }}><X size={16} /></button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {audit.length === 0 && <p style={{ color: '#64748b', textAlign: 'center', padding: 30 }}>Sem registros.</p>}
          {audit.map(a => (
            <div key={a.id} style={{ padding: '10px 12px', marginBottom: 8, borderRadius: 8, background: 'rgba(15,23,42,0.02)', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                <span style={{ color: '#334155' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                    marginRight: 8, color: a.action === 'create' ? '#15803d' : a.action === 'update' ? '#1d4ed8' : '#dc2626',
                    background: a.action === 'create' ? 'rgba(16,185,129,0.15)' : a.action === 'update' ? 'rgba(96,165,250,0.15)' : 'rgba(239,68,68,0.15)',
                  }}>{a.action.toUpperCase()}</span>
                  regra #{a.rule_id || '?'} {a.actor && <>· por <code style={{ color: '#b45309' }}>{a.actor}</code></>}
                </span>
                <span style={{ color: '#64748b', fontFamily: monoFont, fontSize: 10.5 }}>{fmtDateBR(a.at)}</span>
              </div>
              {a.before && <details style={{ marginTop: 6 }}><summary style={{ fontSize: 10.5, color: '#475569', cursor: 'pointer' }}>Antes</summary><pre style={preStyle}>{JSON.stringify(a.before, null, 2)}</pre></details>}
              {a.after && <details style={{ marginTop: 4 }}><summary style={{ fontSize: 10.5, color: '#475569', cursor: 'pointer' }}>Depois</summary><pre style={preStyle}>{JSON.stringify(a.after, null, 2)}</pre></details>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const preStyle = { fontSize: 10.5, color: '#334155', background: '#f1f5f9', padding: 8, borderRadius: 6, overflow: 'auto', fontFamily: monoFont, marginTop: 4 };

function PreviewModal({ unit, site, dataInicio, dataFim, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await getB2BPreview(dataInicio, dataFim, unit, site, 300); setData(r.data); }
      catch { setData({ pedidos: [] }); }
      setLoading(false);
    })();
  }, [unit, site, dataInicio, dataFim]);
  const c = UNIT_COLORS[unit] || '#6b7280';
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 85, background: 'rgba(255,255,255,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#f8fafc', border: `1px solid ${c}50`, borderRadius: 14,
        width: '100%', maxWidth: 1100, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Pedidos · {unit} · {(site || 'todos').toUpperCase()}</p>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#475569' }}><X size={16} /></button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && <p style={{ padding: 30, color: '#475569', textAlign: 'center' }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Carregando…</p>}
          {!loading && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f4f6fb' }}>
                <tr>
                  {['#', 'Data', 'Site', 'Loja WC', 'Cidade', 'Método', 'Total', 'Status', 'Regra'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.pedidos || []).map((p, i) => (
                  <tr key={i} style={{ background: i % 2 ? 'rgba(15,23,42,0.02)' : 'transparent', borderBottom: '1px solid rgba(226,232,240,0.8)' }}>
                    <td style={{ padding: '7px 12px', fontFamily: monoFont, color: '#b45309', fontWeight: 700 }}>{p.orderNumber}</td>
                    <td style={{ padding: '7px 12px', color: '#475569', fontFamily: monoFont }}>{p.dateCreated ? new Date(p.dateCreated).toLocaleDateString('pt-BR') : '—'}</td>
                    <td style={{ padding: '7px 12px' }}>
                      <span style={{ padding: '2px 7px', borderRadius: 999, background: p.site === 'b2b' ? 'rgba(168,85,247,0.18)' : 'rgba(96,165,250,0.18)', color: p.site === 'b2b' ? '#6d28d9' : '#1d4ed8', fontSize: 10, fontWeight: 800 }}>{p.site.toUpperCase()}</span>
                    </td>
                    <td style={{ padding: '7px 12px', color: '#334155', fontSize: 11 }}>{p.storeName}</td>
                    <td style={{ padding: '7px 12px', color: '#334155' }}>{p.billingCity}{p.billingState ? `/${p.billingState}` : ''}</td>
                    <td style={{ padding: '7px 12px', color: '#475569', fontSize: 10.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.shippingMethod}>{p.shippingMethod}</td>
                    <td style={{ padding: '7px 12px', color: '#b45309', fontFamily: monoFont, fontWeight: 700 }}>{fmtBRL(p.total)}</td>
                    <td style={{ padding: '7px 12px', color: '#475569', fontSize: 10.5 }}>{p.status}</td>
                    <td style={{ padding: '7px 12px', color: '#64748b', fontSize: 10.5 }} title={p.rule_nome}>{p.rule_nome ? `#${p.rule_id}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function B2BSalesPanel({ dataInicio, dataFim }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null); // { unit, site }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await getB2BAnalytics(dataInicio, dataFim);
      setAnalytics(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Erro ao carregar');
    }
    setLoading(false);
  }, [dataInicio, dataFim]);
  useEffect(() => { load(); }, [load]);

  return (
    <section style={{ marginTop: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Globe size={18} color="#60a5fa" />
          <h2 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Faturamento E-commerce por Loja</h2>
          {loading && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: '#fbbf24' }} />}
        </div>
        <button onClick={() => setRulesOpen(true)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)',
          background: 'rgba(15,23,42,0.04)', color: '#334155',
          fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        }}>
          <Settings size={13} /> Regras de roteamento
        </button>
      </div>

      <p style={{ fontSize: 11.5, color: '#475569', marginBottom: 14, fontFamily: monoFont }}>
        Fonte: <code style={{ color: '#b45309' }}>b2b.upehelp.com.br</code> + <code style={{ color: '#b45309' }}>upehelp.com.br</code> · Excluindo cancelados, reembolsados, falhados, antifraude e duplicados.
      </p>

      {error && (
        <div style={{ padding: 14, background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#fca5a5', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {!error && analytics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
          <ChartCard
            titulo="Site B2C (consumidor final)"
            icon={ShoppingCart}
            accent="#60a5fa"
            total={analytics.b2c.total}
            count={analytics.b2c.count}
            dados={analytics.b2c.por_unidade}
            onClickUnit={(unit) => setPreviewOpen({ unit, site: 'b2c' })}
          />
          <ChartCard
            titulo="Site B2B (atacado)"
            icon={Globe}
            accent="#a855f7"
            total={analytics.b2b.total}
            count={analytics.b2b.count}
            dados={analytics.b2b.por_unidade}
            onClickUnit={(unit) => setPreviewOpen({ unit, site: 'b2b' })}
          />
        </div>
      )}

      {rulesOpen && <RulesModal onClose={() => { setRulesOpen(false); load(); }} />}
      {previewOpen && (
        <PreviewModal
          unit={previewOpen.unit}
          site={previewOpen.site}
          dataInicio={dataInicio}
          dataFim={dataFim}
          onClose={() => setPreviewOpen(null)}
        />
      )}
    </section>
  );
}

export default B2BSalesPanel;
